import React, { useState, useEffect, useRef } from "react";
import TransactionDetailsModal from "./TransactionDetailsModal";
import { useCheckout } from "../../context/CheckoutContext";
import "./PaymentSuccessModal.css";

function PaymentSuccessModal({ show, transactionId, onClose }) {
  const { brandColor } = useCheckout();
  const themeStyle = brandColor
    ? { "--altruon-brand-color": brandColor }
    : undefined;
  const [copied, setCopied] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const showToast = (message, isSuccess = true) => {
    const toast = document.createElement('div');
    toast.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background-color: ${isSuccess ? '#01AB4B' : '#dc3545'};
        color: white;
        padding: 12px 20px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.3s ease;
        animation: slideIn 0.3s ease-out;
      ">
        ${message}
      </div>
      <style>
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      </style>
    `;
    document.body.appendChild(toast);

    // Remove toast after 2.5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, 300);
      }
    }, 2500);
  };

// Otherwise, keep the copy-to-clipboard functionality
  const copyToClipboardFallback = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  };

  const handleCopyToClipboard = async (e) => {
    e.stopPropagation();

    let copySuccess = false;

    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard?.writeText(transactionId).catch(() => {});
        copySuccess = true;
      } catch (err) {
        console.warn('Modern clipboard API failed, trying fallback:', err);
        copySuccess = copyToClipboardFallback(transactionId);

      }
    } else {
      // Use fallback method
      copySuccess = copyToClipboardFallback(transactionId);
    }

    if (copySuccess) {
      setCopied(true);
      showToast('✓ Copied to clipboard!', true);

      // clear previous timeout (important for rapid clicks)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 5000);

    } else {
      showToast('⚠ Failed to copy', false);
    }
  };

  if (!show) return null;

  // DETAILS VIEW — TransactionDetailsModal renders its own full-screen overlay,
  // so it must NOT be nested inside psm-overlay/psm-content below. Nesting two
  // `position: fixed; inset: 0` overlays stacked the success card behind the
  // details card and doubled up the backdrop blur/dim.
  if (showTransactionDetails) {
    return (
      <TransactionDetailsModal
        transactionId={transactionId}
        showModal={true}
        setShowModal={setShowTransactionDetails}
      />
    );
  }

  return (
    <div className="psm-overlay" style={themeStyle}>
      <div
        className="psm-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* SUCCESS VIEW */}
        <div className="psm-success-message">
          <h2>Payment Successful!</h2>

          {transactionId && (
            <div className="psm-transaction-id-box">
              <div className="psm-txn-content">
                <span className="psm-txn-label">Transaction ID</span>
                <span className="psm-txn-value">{transactionId}</span>
              </div>

              <button className="psm-copy-button" onClick={handleCopyToClipboard}>
                {copied ? "✓" : "Copy"}
              </button>
            </div>
          )}

          <div className="psm-modal-actions">
            <button
              className="psm-details-button"
              onClick={() => setShowTransactionDetails(true)}
            >
              View Details
            </button>

            <button className="psm-close-button" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccessModal;